import { useCallback, useState } from 'react';
import { templatesAPI, projectsAPI } from '../services/api';
import { getExportResolution, prepareElementsForPuppeteerExport } from '../utils/puppeteerExportUtils';

export const usePublishProject = () => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewStage, setPreviewStage] = useState('');
  const [error, setError] = useState(null);

  /**
   * Publish project as template (without preview video)
   */
  const publishProject = useCallback(async (project, options = {}) => {
    setIsPublishing(true);
    setError(null);

    try {
      const {
        name = project.name,
        description = '',
        category = 'other',
        tags = []
      } = options;

      // Call the API to publish the project as a template
      const response = await templatesAPI.publishTemplate({
        projectId: project._id,
        name,
        description,
        category,
        tags
      });

      console.log('Template published successfully:', response.data);
      
      setIsPublishing(false);
      return response.data;
    } catch (err) {
      console.error('Error publishing template:', err);
      setError(err.response?.data?.message || 'Failed to publish template');
      setIsPublishing(false);
      throw err;
    }
  }, []);

  /**
   * Generate preview video for a template using puppeteer export
   * Fetches template data and prepares elements client-side using existing utilities
   * @param {string} templateId - The template ID to generate preview for
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} - The updated template with preview video
   */
  const generatePreviewVideo = useCallback(async (templateId, onProgress) => {
    setIsGeneratingPreview(true);
    setPreviewProgress(0);
    setPreviewStage('initializing');
    setError(null);

    try {
      // Step 1: Fetch the template data
      console.log('[Preview] Fetching template data for:', templateId);
      if (onProgress) onProgress('initializing', 0, 'Fetching template data...');

      const templateResponse = await templatesAPI.getById(templateId);
      const template = templateResponse.data.data.template;

      if (!template) {
        throw new Error('Template not found');
      }

      console.log('[Preview] Template fetched:', template.name);
      console.log('[Preview] Layers:', template.layers?.length || 0);

      // Step 2: Prepare elements for export using client-side utility
      setPreviewStage('preparing');
      setPreviewProgress(5);
      if (onProgress) onProgress('preparing', 5, 'Preparing export data...');

      const preparedElements = prepareElementsForPuppeteerExport(template.layers || []);
      const { width: exportWidth, height: exportHeight } = getExportResolution({
        width: template.canvas?.width || 1920,
        height: template.canvas?.height || 1080
      });

      // Build export payload (same format as normal puppeteer export)
      const exportPayload = {
        projectId: template.sourceProjectId || templateId,
        projectName: `template_preview_${templateId}`,
        fps: 25,
        duration: template.duration || 10,
        canvasWidth: template.canvas?.width || 1920,
        canvasHeight: template.canvas?.height || 1080,
        exportWidth,
        exportHeight,
        elements: preparedElements,
        backgroundColor: '#000000',
        format: 'mp4'
      };

      console.log('[Preview] Export payload prepared:', {
        elements: preparedElements.length,
        duration: exportPayload.duration,
        dimensions: `${exportWidth}x${exportHeight}`
      });

      // Step 3: Start the puppeteer export
      setPreviewStage('starting_export');
      setPreviewProgress(10);
      if (onProgress) onProgress('starting_export', 10, 'Starting video export...');

      const puppeteerResponse = await projectsAPI.initializePuppeteerExport(exportPayload);
      const jobId = puppeteerResponse.data.jobId;

      console.log('[Preview] Puppeteer export started with job ID:', jobId);

      // Step 4: Poll for export status
      let exportComplete = false;
      let pollAttempts = 0;
      const MAX_POLL_ATTEMPTS = 600; // 10 minutes max
      let currentPollInterval = 2000;
      const MAX_POLL_INTERVAL = 10000;

      while (!exportComplete && pollAttempts < MAX_POLL_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, currentPollInterval));
        currentPollInterval = Math.min(currentPollInterval * 1.2, MAX_POLL_INTERVAL);
        pollAttempts++;

        try {
          const statusResponse = await projectsAPI.getPuppeteerExportStatus({ jobId });
          const jobData = statusResponse.data.data || statusResponse.data;
          const { status, progress, stage, message, error: jobError } = jobData;

          console.log(`[Preview Poll ${pollAttempts}] ${stage}: ${progress}% (status: ${status})`);

          // Check for failed/cancelled status
          if (status === 'failed' || status === 'cancelled' || stage === 'error') {
            throw new Error(jobError || 'Preview video export failed');
          }

          // Update progress (scale from 10-95%)
          const scaledProgress = Math.round(10 + (progress * 0.85));
          setPreviewProgress(scaledProgress);
          setPreviewStage(stage || 'processing');
          if (onProgress) {
            onProgress(stage, scaledProgress, message || `${stage}... ${Math.round(scaledProgress)}%`);
          }

          // Check if completed
          if (status === 'completed' || stage === 'complete') {
            exportComplete = true;
            console.log('[Preview] Export completed!');
          }

        } catch (pollError) {
          // Handle 429 rate limit
          if (pollError.response && pollError.response.status === 429) {
            console.warn('[Preview] Rate limited, backing off...');
            await new Promise(resolve => setTimeout(resolve, 15000));
            continue;
          }
          throw pollError;
        }
      }

      if (!exportComplete) {
        throw new Error('Preview video export timeout');
      }

      // Step 5: Update template with the preview video
      setPreviewStage('finalizing');
      setPreviewProgress(95);
      if (onProgress) onProgress('finalizing', 95, 'Saving preview video...');

      const updateResponse = await templatesAPI.updatePreviewVideo(templateId, jobId);
      
      console.log('[Preview] Template updated with preview video:', updateResponse.data);

      setPreviewProgress(100);
      setPreviewStage('complete');
      if (onProgress) onProgress('complete', 100, 'Preview video generated!');

      setIsGeneratingPreview(false);
      return updateResponse.data;

    } catch (err) {
      console.error('Error generating preview video:', err);
      setError(err.response?.data?.message || err.message || 'Failed to generate preview video');
      setPreviewStage('error');
      setIsGeneratingPreview(false);
      throw err;
    }
  }, []);

  /**
   * Publish project as template AND generate preview video
   * @param {Object} project - The project to publish
   * @param {Object} options - Publishing options (name, description, category, tags)
   * @param {Function} onProgress - Optional progress callback for preview generation
   * @returns {Promise<Object>} - The published template with preview video
   */
  const publishProjectWithPreview = useCallback(async (project, options = {}, onProgress) => {
    setIsPublishing(true);
    setError(null);

    try {
      // Step 1: Publish the template first
      console.log('[Publish] Publishing template...');
      if (onProgress) onProgress('publishing', 0, 'Publishing template...');

      const publishResponse = await publishProject(project, options);
      const template = publishResponse.data.template;

      console.log('[Publish] Template published:', template._id);

      // Step 2: Generate preview video
      console.log('[Publish] Generating preview video...');
      if (onProgress) onProgress('generating_preview', 5, 'Generating preview video...');

      const previewResponse = await generatePreviewVideo(template._id, (stage, progress, message) => {
        // Adjust progress to account for publishing step (0-5%)
        const adjustedProgress = 5 + (progress * 0.95);
        if (onProgress) onProgress(stage, adjustedProgress, message);
      });

      setIsPublishing(false);
      return previewResponse;

    } catch (err) {
      console.error('Error publishing template with preview:', err);
      setError(err.response?.data?.message || err.message || 'Failed to publish template');
      setIsPublishing(false);
      throw err;
    }
  }, [publishProject, generatePreviewVideo]);

  return {
    // Basic publish (without preview)
    publishProject,
    isPublishing,
    error,
    
    // Preview video generation
    generatePreviewVideo,
    isGeneratingPreview,
    previewProgress,
    previewStage,
    
    // Combined publish with preview
    publishProjectWithPreview
  };
};